import React, { useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  SimpleGrid,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import IconBox from 'components/icons/IconBox';
import MiniStatistics from 'components/card/MiniStatistics';
import Button from 'components/ui/Button';
import { useEpisodesAll } from 'hooks/useEpisodes';
import { usePlansAll } from 'hooks/usePlans';
import { usePaymentsAll } from 'hooks/usePayments';
import PaymentRegisterModal from 'components/layouts/patients/PaymentRegisterModal';
import { useNavigate } from 'react-router-dom';
import {
  MdArrowForwardIos,
  MdCalendarMonth,
  MdCheckCircle,
  MdCreditCard,
  MdFitnessCenter,
  MdHourglassEmpty,
  MdNoteAlt,
  MdOpenInNew,
  MdRemoveCircle,
  MdSearch,
} from 'react-icons/md';
import {
  ClinicalEpisode,
  EstadoEpisodio,
  EstadoPago,
  EstadoPlan,
  MetodoPago,
  SessionPayment,
  TipoCita,
  TreatmentPlan,
} from 'types/models';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const months = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtMoney(n: number): string {
  return `$${Number(n).toFixed(2)}`;
}

function patientName(nombres?: string, apellidos?: string): string {
  if (!nombres && !apellidos) return '—';
  return [nombres, apellidos].filter(Boolean).join(' ');
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ESTADO_EP_BADGE: Record<EstadoEpisodio, string> = {
  [EstadoEpisodio.ABIERTO]:        'green',
  [EstadoEpisodio.EN_TRATAMIENTO]: 'blue',
  [EstadoEpisodio.CERRADO]:        'gray',
  [EstadoEpisodio.ARCHIVADO]:      'blackAlpha',
};
const ESTADO_EP_LABEL: Record<EstadoEpisodio, string> = {
  [EstadoEpisodio.ABIERTO]:        'Abierto',
  [EstadoEpisodio.EN_TRATAMIENTO]: 'En tratamiento',
  [EstadoEpisodio.CERRADO]:        'Cerrado',
  [EstadoEpisodio.ARCHIVADO]:      'Archivado',
};

const ESTADO_PLAN_BADGE: Record<EstadoPlan, string> = {
  [EstadoPlan.BORRADOR]:   'orange',
  [EstadoPlan.ACTIVO]:     'blue',
  [EstadoPlan.COMPLETADO]: 'green',
  [EstadoPlan.CANCELADO]:  'red',
};
const ESTADO_PLAN_LABEL: Record<EstadoPlan, string> = {
  [EstadoPlan.BORRADOR]:   'Borrador',
  [EstadoPlan.ACTIVO]:     'Activo',
  [EstadoPlan.COMPLETADO]: 'Completado',
  [EstadoPlan.CANCELADO]:  'Cancelado',
};

const ESTADO_PAGO_BADGE: Record<EstadoPago, string> = {
  [EstadoPago.PENDIENTE]: 'orange',
  [EstadoPago.PARCIAL]:   'blue',
  [EstadoPago.PAGADO]:    'green',
};
const ESTADO_PAGO_LABEL: Record<EstadoPago, string> = {
  [EstadoPago.PENDIENTE]: 'Pendiente',
  [EstadoPago.PARCIAL]:   'Parcial',
  [EstadoPago.PAGADO]:    'Pagado',
};
const ESTADO_PAGO_ICON: Record<EstadoPago, React.ElementType> = {
  [EstadoPago.PENDIENTE]: MdHourglassEmpty,
  [EstadoPago.PARCIAL]:   MdRemoveCircle,
  [EstadoPago.PAGADO]:    MdCheckCircle,
};
const METODO_LABEL: Record<MetodoPago, string> = {
  [MetodoPago.EFECTIVO]:      'Efectivo',
  [MetodoPago.TRANSFERENCIA]: 'Transferencia',
  [MetodoPago.TARJETA]:       'Tarjeta',
  [MetodoPago.SEGURO]:        'Seguro',
};
const TIPO_LABEL: Record<TipoCita, string> = {
  [TipoCita.PRIMERA_VEZ]:   'Consulta normal',
  [TipoCita.SEGUIMIENTO]:   'Seguimiento',
  [TipoCita.INTERCONSULTA]: 'Interconsulta',
};

// ─── Filter chips ─────────────────────────────────────────────────────────────

interface ChipProps {
  label: string;
  active: boolean;
  colorScheme?: string;
  onClick: () => void;
}

function FilterChip({ label, active, colorScheme = 'brand', onClick }: ChipProps) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      as="button"
      px="14px" py="6px" borderRadius="full" fontSize="xs" fontWeight="700"
      cursor="pointer" border="1.5px solid" transition="all 0.15s"
      bg={active ? `${colorScheme}.500` : 'transparent'}
      borderColor={active ? `${colorScheme}.500` : borderColor}
      color={active ? 'white' : mutedColor}
      _hover={{ borderColor: `${colorScheme}.400` }}
      onClick={onClick}
    >
      {label}
    </Box>
  );
}

// ─── Episode row ──────────────────────────────────────────────────────────────

function EpisodeRow({ ep, onClick }: { ep: ClinicalEpisode; onClick: () => void }) {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover     = useColorModeValue('gray.50', 'navy.700');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const isActive    = ep.estado === EstadoEpisodio.ABIERTO || ep.estado === EstadoEpisodio.EN_TRATAMIENTO;

  return (
    <Flex
      align="center" gap="14px" px="20px" py="14px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover, cursor: 'pointer' }} transition="background 0.15s"
      onClick={onClick}
    >
      <Flex
        w="36px" h="36px" borderRadius="10px" flexShrink={0} align="center" justify="center"
        bg={isActive ? 'blue.50' : 'gray.100'}
      >
        <Icon as={MdNoteAlt} w="16px" h="16px" color={isActive ? 'blue.500' : 'gray.400'} />
      </Flex>

      <Box flex="1" minW={0}>
        <Flex align="center" gap="8px" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="700" color={textColor}>
            {patientName(ep.paciente?.nombres, ep.paciente?.apellidos)}
          </Text>
          <Badge colorScheme="purple" variant="subtle" borderRadius="full" px="8px" fontSize="10px">
            {ep.codigoHc}
          </Badge>
          <Badge colorScheme={ESTADO_EP_BADGE[ep.estado]} borderRadius="full" px="8px" fontSize="10px">
            {ESTADO_EP_LABEL[ep.estado]}
          </Badge>
        </Flex>
        <Text fontSize="xs" color={textColor} mt="2px" noOfLines={1}>{ep.motivoConsulta}</Text>
        {ep.diagnosticoPrincipal && (
          <Text fontSize="xs" color={mutedColor} noOfLines={1}>Dx: {ep.diagnosticoPrincipal}</Text>
        )}
      </Box>

      <Flex direction="column" align="flex-end" gap="2px" flexShrink={0}>
        <Text fontSize="xs" color={mutedColor}>{fmtDate(ep.fechaApertura)}</Text>
        {ep.codigoCie10 && (
          <Badge colorScheme="gray" variant="subtle" borderRadius="full" px="8px" fontSize="10px">
            {ep.codigoCie10}
          </Badge>
        )}
      </Flex>

      <Icon as={MdArrowForwardIos} w="12px" h="12px" color={mutedColor} flexShrink={0} />
    </Flex>
  );
}

// ─── Plan row ─────────────────────────────────────────────────────────────────

function PlanRow({ plan, onClick }: { plan: TreatmentPlan; onClick: () => void }) {
  const borderColor  = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover      = useColorModeValue('gray.50', 'navy.700');
  const textColor    = useColorModeValue('gray.800', 'white');
  const mutedColor   = useColorModeValue('gray.500', 'gray.400');
  const progressBg   = useColorModeValue('gray.100', 'navy.600');
  const progreso     = Number(plan.progresoPorcentaje);

  return (
    <Flex
      align="center" gap="14px" px="20px" py="14px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover, cursor: 'pointer' }} transition="background 0.15s"
      onClick={onClick}
    >
      <Flex
        w="36px" h="36px" borderRadius="10px" flexShrink={0} align="center" justify="center"
        bg={plan.estado === EstadoPlan.ACTIVO ? 'brand.50' : 'gray.100'}
      >
        <Icon
          as={MdFitnessCenter} w="16px" h="16px"
          color={plan.estado === EstadoPlan.ACTIVO ? 'brand.500' : 'gray.400'}
        />
      </Flex>

      <Box flex="1" minW={0}>
        <Flex align="center" gap="8px" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="700" color={textColor}>
            {patientName(plan.paciente?.nombres, plan.paciente?.apellidos)}
          </Text>
          <Badge colorScheme="purple" variant="subtle" borderRadius="full" px="8px" fontSize="10px">
            {plan.codigoHc}
          </Badge>
          <Badge colorScheme={ESTADO_PLAN_BADGE[plan.estado]} borderRadius="full" px="8px" fontSize="10px">
            {ESTADO_PLAN_LABEL[plan.estado]}
          </Badge>
        </Flex>
        <Text fontSize="xs" color={textColor} mt="2px" noOfLines={1}>{plan.objetivoTerapeutico}</Text>
        <Flex align="center" gap="8px" mt="4px">
          <Box flex="1" maxW="120px">
            <Progress
              value={progreso} size="xs" borderRadius="full" colorScheme="brand"
              bg={progressBg}
            />
          </Box>
          <Text fontSize="10px" color={mutedColor} fontWeight="700">{progreso}%</Text>
          {plan.frecuenciaSemanal && (
            <Text fontSize="10px" color={mutedColor}>{plan.frecuenciaSemanal}x/sem</Text>
          )}
        </Flex>
      </Box>

      <Flex direction="column" align="flex-end" gap="2px" flexShrink={0}>
        {plan.fechaInicio && (
          <Text fontSize="xs" color={mutedColor}>{fmtDate(plan.fechaInicio)}</Text>
        )}
        {plan.duracionEstimadaSemanas && (
          <Text fontSize="xs" color={mutedColor}>{plan.duracionEstimadaSemanas} sem</Text>
        )}
      </Flex>

      <Icon as={MdArrowForwardIos} w="12px" h="12px" color={mutedColor} flexShrink={0} />
    </Flex>
  );
}

// ─── Payment row (global) ─────────────────────────────────────────────────────

interface GlobalPaymentRowProps {
  payment: SessionPayment;
  onRegister: (p: SessionPayment) => void;
}

function GlobalPaymentRow({ payment, onRegister }: GlobalPaymentRowProps) {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover     = useColorModeValue('gray.50', 'navy.700');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const isPaid      = payment.estadoPago === EstadoPago.PAGADO;

  const patient = payment.appointment?.patient;
  const dateStr = payment.appointment?.scheduledAt
    ? fmtDate(payment.appointment.scheduledAt)
    : fmtDate(payment.createdAt);

  return (
    <Flex
      align="center" gap="14px" px="20px" py="14px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover }} transition="background 0.15s"
    >
      <Flex
        w="36px" h="36px" borderRadius="10px" flexShrink={0} align="center" justify="center"
        bg={isPaid ? 'green.50' : payment.estadoPago === EstadoPago.PARCIAL ? 'blue.50' : 'orange.50'}
      >
        <Icon
          as={ESTADO_PAGO_ICON[payment.estadoPago]} w="16px" h="16px"
          color={isPaid ? 'green.500' : payment.estadoPago === EstadoPago.PARCIAL ? 'blue.500' : 'orange.400'}
        />
      </Flex>

      <Box flex="1" minW={0}>
        <Flex align="center" gap="8px" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="700" color={textColor}>
            {patient ? patientName(patient.nombres, patient.apellidos) : '—'}
          </Text>
          <Text fontSize="sm" fontWeight="700" color={textColor}>{fmtMoney(payment.monto)}</Text>
          <Badge colorScheme={ESTADO_PAGO_BADGE[payment.estadoPago]} borderRadius="full" px="8px" fontSize="10px">
            {ESTADO_PAGO_LABEL[payment.estadoPago]}
          </Badge>
          {payment.metodoPago && (
            <Badge colorScheme="gray" variant="subtle" borderRadius="full" px="8px" fontSize="10px">
              {METODO_LABEL[payment.metodoPago]}
            </Badge>
          )}
          {payment.appointment?.tipoCita && (
            <Badge colorScheme="purple" variant="subtle" borderRadius="full" px="8px" fontSize="10px">
              {TIPO_LABEL[payment.appointment.tipoCita]}
            </Badge>
          )}
        </Flex>
        <Text fontSize="xs" color={mutedColor} mt="2px">{dateStr}</Text>
        {payment.fechaPago && (
          <Text fontSize="xs" color="green.500" mt="1px">Pagado: {fmtDate(payment.fechaPago)}</Text>
        )}
      </Box>

      {payment.estadoPago !== EstadoPago.PAGADO && (
        <Button
          size="sm" colorScheme="brand" variant="outline" flexShrink={0}
          onClick={() => onRegister(payment)}
        >
          Registrar
        </Button>
      )}
    </Flex>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ icon, label }: { icon: React.ElementType; label: string }) {
  const emptyBg   = useColorModeValue('brand.50', 'navy.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Flex direction="column" align="center" py="48px" gap="12px">
      <Flex w="52px" h="52px" bg={emptyBg} borderRadius="16px" align="center" justify="center">
        <Icon as={icon} w="22px" h="22px" color="brand.500" />
      </Flex>
      <Text fontSize="sm" fontWeight="600" color={textColor}>{label}</Text>
    </Flex>
  );
}

// ─── Episodes tab ─────────────────────────────────────────────────────────────

function EpisodiosTab() {
  const navigate    = useNavigate();
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const cardBg      = useColorModeValue('white', 'navy.800');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');

  const [search, setSearch]   = useState('');
  const [estado, setEstado]   = useState<EstadoEpisodio | ''>('');

  const params = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(estado ? { estado } : {}),
  };

  const { data: episodesData, isLoading, isError, error } = useEpisodesAll(params);
  const episodes: ClinicalEpisode[] = episodesData ?? [];

  const stats = {
    total:       episodes.length,
    activos:     episodes.filter((e: ClinicalEpisode) => e.estado === EstadoEpisodio.ABIERTO || e.estado === EstadoEpisodio.EN_TRATAMIENTO).length,
    cerrados:    episodes.filter((e: ClinicalEpisode) => e.estado === EstadoEpisodio.CERRADO).length,
  };

  return (
    <Box p="20px">
      {/* Stats */}
      {!isLoading && episodes.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mb="20px">
          <StatCard label="Total episodios" value={stats.total} color="gray" />
          <StatCard label="Activos" value={stats.activos} color="blue" />
          <StatCard label="Cerrados" value={stats.cerrados} color="gray" />
        </SimpleGrid>
      )}

      {/* Filters */}
      <Flex gap="10px" mb="16px" flexWrap="wrap" align="center">
        <InputGroup w={{ base: 'full', md: '260px' }} size="sm">
          <InputLeftElement pointerEvents="none" h="36px">
            <Icon as={MdSearch} color="gray.400" w="16px" h="16px" />
          </InputLeftElement>
          <Input
            pl="36px" h="36px" bg={inputBg} border="1px solid" borderColor={borderColor}
            borderRadius="10px" fontSize="sm" placeholder="Buscar motivo, diagnóstico..."
            value={search} onChange={e => setSearch(e.target.value)}
            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
          />
        </InputGroup>
        <Flex gap="6px" flexWrap="wrap">
          <FilterChip label="Todos" active={!estado} onClick={() => setEstado('')} />
          <FilterChip label="Abierto"        active={estado === EstadoEpisodio.ABIERTO}        colorScheme="green"    onClick={() => setEstado(estado === EstadoEpisodio.ABIERTO        ? '' : EstadoEpisodio.ABIERTO)} />
          <FilterChip label="En tratamiento" active={estado === EstadoEpisodio.EN_TRATAMIENTO} colorScheme="blue"     onClick={() => setEstado(estado === EstadoEpisodio.EN_TRATAMIENTO ? '' : EstadoEpisodio.EN_TRATAMIENTO)} />
          <FilterChip label="Cerrado"        active={estado === EstadoEpisodio.CERRADO}        colorScheme="gray"     onClick={() => setEstado(estado === EstadoEpisodio.CERRADO        ? '' : EstadoEpisodio.CERRADO)} />
          <FilterChip label="Archivado"      active={estado === EstadoEpisodio.ARCHIVADO}      colorScheme="blackAlpha" onClick={() => setEstado(estado === EstadoEpisodio.ARCHIVADO    ? '' : EstadoEpisodio.ARCHIVADO)} />
        </Flex>
      </Flex>

      {/* List */}
      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        <Flex px="20px" py="12px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="800" color={textColor}>Episodios clínicos</Text>
          {!isLoading && (
            <Text fontSize="xs" color="gray.400">{episodes.length} resultado{episodes.length !== 1 ? 's' : ''}</Text>
          )}
        </Flex>

        {isLoading && (
          <Box px="20px" py="12px">
            {[1,2,3,4].map(i => <Skeleton key={i} h="64px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}

        {!isLoading && isError && (
          <Flex direction="column" align="center" py="32px" gap="8px">
            <Text fontSize="sm" fontWeight="700" color="red.500">Error al cargar episodios</Text>
            <Text fontSize="xs" color="gray.500">{(error as any)?.response?.data?.message ?? (error as any)?.message ?? 'Error desconocido'}</Text>
          </Flex>
        )}

        {!isLoading && !isError && episodes.length === 0 && (
          <EmptyState icon={MdNoteAlt} label="Sin episodios para los filtros aplicados" />
        )}

        {!isLoading && !isError && episodes.map(ep => (
          <EpisodeRow
            key={ep.id}
            ep={ep}
            onClick={() => navigate(`/admin/patients/${ep.pacienteId}`)}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Plans tab ────────────────────────────────────────────────────────────────

function PlanesTab() {
  const navigate    = useNavigate();
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const cardBg      = useColorModeValue('white', 'navy.800');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');

  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState<EstadoPlan | ''>('');

  const params = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(estado ? { estado } : {}),
  };

  const { data: plansData, isLoading } = usePlansAll(params);
  const plans: TreatmentPlan[] = plansData ?? [];

  const stats = {
    total:      plans.length,
    activos:    plans.filter((p: TreatmentPlan) => p.estado === EstadoPlan.ACTIVO).length,
    completados: plans.filter((p: TreatmentPlan) => p.estado === EstadoPlan.COMPLETADO).length,
  };

  return (
    <Box p="20px">
      {/* Stats */}
      {!isLoading && plans.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mb="20px">
          <StatCard label="Total planes" value={stats.total} color="gray" />
          <StatCard label="Activos" value={stats.activos} color="brand" />
          <StatCard label="Completados" value={stats.completados} color="green" />
        </SimpleGrid>
      )}

      {/* Filters */}
      <Flex gap="10px" mb="16px" flexWrap="wrap" align="center">
        <InputGroup w={{ base: 'full', md: '260px' }} size="sm">
          <InputLeftElement pointerEvents="none" h="36px">
            <Icon as={MdSearch} color="gray.400" w="16px" h="16px" />
          </InputLeftElement>
          <Input
            pl="36px" h="36px" bg={inputBg} border="1px solid" borderColor={borderColor}
            borderRadius="10px" fontSize="sm" placeholder="Buscar objetivo terapéutico..."
            value={search} onChange={e => setSearch(e.target.value)}
            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
          />
        </InputGroup>
        <Flex gap="6px" flexWrap="wrap">
          <FilterChip label="Todos"      active={!estado}                             onClick={() => setEstado('')} />
          <FilterChip label="Activo"     active={estado === EstadoPlan.ACTIVO}        colorScheme="brand" onClick={() => setEstado(estado === EstadoPlan.ACTIVO     ? '' : EstadoPlan.ACTIVO)} />
          <FilterChip label="Completado" active={estado === EstadoPlan.COMPLETADO}    colorScheme="green" onClick={() => setEstado(estado === EstadoPlan.COMPLETADO ? '' : EstadoPlan.COMPLETADO)} />
          <FilterChip label="Cancelado"  active={estado === EstadoPlan.CANCELADO}     colorScheme="red"   onClick={() => setEstado(estado === EstadoPlan.CANCELADO  ? '' : EstadoPlan.CANCELADO)} />
        </Flex>
      </Flex>

      {/* List */}
      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        <Flex px="20px" py="12px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="800" color={textColor}>Planes de tratamiento</Text>
          {!isLoading && (
            <Text fontSize="xs" color="gray.400">{plans.length} resultado{plans.length !== 1 ? 's' : ''}</Text>
          )}
        </Flex>

        {isLoading && (
          <Box px="20px" py="12px">
            {[1,2,3,4].map(i => <Skeleton key={i} h="72px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}

        {!isLoading && plans.length === 0 && (
          <EmptyState icon={MdFitnessCenter} label="Sin planes para los filtros aplicados" />
        )}

        {!isLoading && plans.map(plan => (
          <PlanRow
            key={plan.id}
            plan={plan}
            onClick={() => navigate(`/admin/patients/${plan.pacienteId}`)}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Payments tab ─────────────────────────────────────────────────────────────

function CobrosTab() {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const cardBg      = useColorModeValue('white', 'navy.800');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  const [estadoPago, setEstadoPago] = useState<EstadoPago | ''>('');
  const [desde,      setDesde]      = useState('');
  const [hasta,      setHasta]      = useState('');
  const [selected,   setSelected]   = useState<SessionPayment | null>(null);

  const params = {
    ...(estadoPago ? { estadoPago } : {}),
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
  };

  const { data: paymentsData, isLoading } = usePaymentsAll(params);
  const payments: SessionPayment[] = paymentsData ?? [];

  const totalCobrado   = payments.filter((p: SessionPayment) => p.estadoPago === EstadoPago.PAGADO).reduce((s: number, p: SessionPayment) => s + Number(p.monto), 0);
  const totalPendiente = payments.filter((p: SessionPayment) => p.estadoPago !== EstadoPago.PAGADO).reduce((s: number, p: SessionPayment) => s + Number(p.monto), 0);
  const pendientes     = payments.filter((p: SessionPayment) => p.estadoPago !== EstadoPago.PAGADO).length;

  return (
    <Box p="20px">
      {/* Stats */}
      {!isLoading && payments.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mb="20px">
          <StatCard label="Total cobros" value={payments.length} color="gray" />
          <StatCard label="Por cobrar" value={fmtMoney(totalPendiente)} color={pendientes > 0 ? 'orange' : 'gray'} />
          <StatCard label="Cobrado" value={fmtMoney(totalCobrado)} color="green" />
        </SimpleGrid>
      )}

      {/* Filters */}
      <Flex gap="10px" mb="16px" flexWrap="wrap" align="center">
        <Flex gap="6px" flexWrap="wrap">
          <FilterChip label="Todos"     active={!estadoPago}                            onClick={() => setEstadoPago('')} />
          <FilterChip label="Pendiente" active={estadoPago === EstadoPago.PENDIENTE}    colorScheme="orange" onClick={() => setEstadoPago(estadoPago === EstadoPago.PENDIENTE ? '' : EstadoPago.PENDIENTE)} />
          <FilterChip label="Parcial"   active={estadoPago === EstadoPago.PARCIAL}      colorScheme="blue"   onClick={() => setEstadoPago(estadoPago === EstadoPago.PARCIAL   ? '' : EstadoPago.PARCIAL)} />
          <FilterChip label="Pagado"    active={estadoPago === EstadoPago.PAGADO}       colorScheme="green"  onClick={() => setEstadoPago(estadoPago === EstadoPago.PAGADO    ? '' : EstadoPago.PAGADO)} />
        </Flex>
        <Flex gap="8px" align="center">
          <Input
            type="date" size="sm" value={desde} onChange={e => setDesde(e.target.value)}
            borderRadius="10px" w="140px" fontSize="xs"
            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
          />
          <Text fontSize="xs" color={mutedColor}>—</Text>
          <Input
            type="date" size="sm" value={hasta} onChange={e => setHasta(e.target.value)}
            borderRadius="10px" w="140px" fontSize="xs"
            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
          />
        </Flex>
      </Flex>

      {/* List */}
      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        <Flex px="20px" py="12px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="800" color={textColor}>Historial de cobros</Text>
          <Flex align="center" gap="8px">
            {pendientes > 0 && (
              <Badge colorScheme="orange" borderRadius="full" px="10px" fontSize="11px">
                {pendientes} pendiente{pendientes > 1 ? 's' : ''}
              </Badge>
            )}
            {!isLoading && (
              <Text fontSize="xs" color="gray.400">{payments.length} resultado{payments.length !== 1 ? 's' : ''}</Text>
            )}
          </Flex>
        </Flex>

        {isLoading && (
          <Box px="20px" py="12px">
            {[1,2,3,4].map(i => <Skeleton key={i} h="64px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}

        {!isLoading && payments.length === 0 && (
          <EmptyState icon={MdCreditCard} label="Sin cobros para los filtros aplicados" />
        )}

        {!isLoading && payments.map(payment => (
          <GlobalPaymentRow key={payment.id} payment={payment} onRegister={setSelected} />
        ))}
      </Box>

      {selected && (
        <PaymentRegisterModal
          isOpen
          onClose={() => setSelected(null)}
          payment={selected}
        />
      )}
    </Box>
  );
}

// ─── Stat card helper ─────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  const cardBg      = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex
      bg={color !== 'gray' ? `${color}.50` : cardBg}
      border="1px solid"
      borderColor={color !== 'gray' ? `${color}.100` : borderColor}
      borderRadius="14px" px="16px" py="12px" direction="column" gap="2px"
    >
      <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider"
        color={color !== 'gray' ? `${color}.500` : mutedColor}>
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="800" color={color !== 'gray' ? `${color}.500` : textColor}>
        {value}
      </Text>
    </Flex>
  );
}

// ─── Tab config ───────────────────────────────────────────────────────────────

const TABS = [
  { label: 'Episodios',          icon: MdNoteAlt,      component: <EpisodiosTab /> },
  { label: 'Planes',             icon: MdFitnessCenter, component: <PlanesTab /> },
  { label: 'Cobros',             icon: MdCreditCard,    component: <CobrosTab /> },
];

// ─── Main view ────────────────────────────────────────────────────────────────

export default function ClinicaView() {
  const tabColor       = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const activeTabColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor    = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor      = useColorModeValue('secondaryGray.900', 'white');

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Header */}
      <Flex direction="column" mb="24px">
        <Text color={textColor} fontSize="2xl" fontWeight="800">Gestión Clínica</Text>
        <Text color="secondaryGray.600" fontSize="sm">
          Episodios, planes de tratamiento y cobros del sistema
        </Text>
      </Flex>

      {/* Tabs card */}
      <Card p="0" overflow="hidden">
        <Tabs colorScheme="brand" isLazy>
          <Box borderBottom="1px solid" borderColor={borderColor}>
            <TabList
              px="20px" gap="0" overflowX="auto"
              sx={{
                '::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {TABS.map(tab => (
                <Tab
                  key={tab.label}
                  px="16px" py="16px" fontSize="sm" fontWeight="600"
                  color={tabColor} whiteSpace="nowrap"
                  display="flex" alignItems="center" gap="6px"
                  _selected={{ color: activeTabColor, fontWeight: '800', borderBottomColor: 'brand.500', borderBottomWidth: '2px' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  <Icon as={tab.icon} w="16px" h="16px" />
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Box>

          <TabPanels>
            {TABS.map(tab => (
              <TabPanel key={tab.label} p="0">
                {tab.component}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>
    </Box>
  );
}
